import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getSharedScenario, fmt, fmtFull, fmtPct, fmtNum } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, TrendingUp, Percent, Calculator, AlertCircle } from "lucide-react";

export default function SharedView() {
  const { token } = useParams();
  const [scenario, setScenario] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("developer");

  useEffect(() => {
    getSharedScenario(token)
      .then(setScenario)
      .catch(() => setError("This share link is invalid or has been revoked."));
  }, [token]);

  if (error) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8f9fb" }}>
        <Card style={{ width: 440, textAlign: "center" }}>
          <CardContent style={{ padding: "48px 32px" }}>
            <AlertCircle size={40} style={{ margin: "0 auto 16px", color: "#ef4444" }} />
            <h2 style={{ fontFamily: "Manrope", fontWeight: 700, fontSize: 20, marginBottom: 8 }}>Link Unavailable</h2>
            <p style={{ color: "#64748b", fontSize: 14 }} data-testid="shared-error">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!scenario) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8f9fb" }}>
        <p style={{ color: "#64748b" }}>Loading shared appraisal...</p>
      </div>
    );
  }

  const { results, inputs } = scenario;
  const kpis = results?.kpis;

  const kpiCards = kpis ? [
    { label: "Gross Dev Value", value: fmt(kpis.total_gdv), sub: `${fmtNum(kpis.total_units)} units`, icon: Building2 },
    { label: "Dev Profit", value: fmt(kpis.dev_profit), sub: `${fmtPct(kpis.dev_profit_on_cost)} on cost`, icon: TrendingUp },
    { label: "Ungeared IRR", value: fmtPct(kpis.ungeared_irr), sub: `Multiple: ${kpis.ungeared_multiple?.toFixed(2)}x`, icon: Percent },
    { label: "Geared IRR", value: fmtPct(kpis.geared_irr), sub: `Multiple: ${kpis.geared_multiple?.toFixed(2)}x`, icon: Calculator },
  ] : [];

  const ut = ["Studio", "1-Bed", "2-Bed", "3-Bed"];

  return (
    <div style={{ minHeight: "100vh", background: "#f8f9fb" }}>
      {/* Header bar */}
      <div style={{ background: "#1a1a2e", padding: "14px 32px", display: "flex", alignItems: "center", gap: 12 }}>
        <img src="/toro-logo.png" alt="Toro Strategic" style={{ height: 32, filter: "brightness(10)" }} />
        <span style={{ color: "white", fontFamily: "Manrope", fontWeight: 600, fontSize: 14, opacity: 0.7 }}>Shared Appraisal</span>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 24px" }} data-testid="shared-view">
        {/* Title */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontFamily: "Manrope", fontWeight: 800, fontSize: 26, color: "#1a1a2e" }} data-testid="shared-title">
            {scenario.project_name || "Untitled Appraisal"}
          </h1>
          <p style={{ color: "#64748b", fontSize: 14, marginTop: 4 }}>
            {scenario.location || "No location"} &middot; {scenario.created_at ? new Date(scenario.created_at).toLocaleDateString("en-GB") : ""}
          </p>
          <div style={{ display: "inline-block", marginTop: 8, padding: "4px 12px", borderRadius: 20, background: "#e0f2fe", color: "#0369a1", fontSize: 12, fontWeight: 600 }}>
            Read-only &middot; Shared link
          </div>
        </div>

        {/* KPI cards */}
        {kpiCards.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 32 }} data-testid="shared-kpi-grid">
            {kpiCards.map((kpi, i) => (
              <div key={i} className="kpi-card animate-in" style={{ animationDelay: `${i * 50}ms` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div className="kpi-label">{kpi.label}</div>
                  <kpi.icon size={16} style={{ color: "#2563eb" }} />
                </div>
                <div className="kpi-value" data-testid={`shared-kpi-${kpi.label.toLowerCase().replace(/\s/g, "-")}`}>{kpi.value}</div>
                <div className="kpi-sub">{kpi.sub}</div>
              </div>
            ))}
          </div>
        )}

        {/* Appraisal tables */}
        {results && (
          <Tabs value={tab} onValueChange={setTab} data-testid="shared-result-tabs">
            <TabsList>
              <TabsTrigger value="developer" data-testid="shared-tab-developer">Developer Appraisal</TabsTrigger>
              <TabsTrigger value="funder" data-testid="shared-tab-funder">Funder Appraisal</TabsTrigger>
            </TabsList>

            <TabsContent value="developer" style={{ marginTop: 20 }}>
              <div style={{ display: "grid", gap: 16 }}>
                {/* Development Value */}
                <Card>
                  <CardHeader><CardTitle style={{ fontSize: 15 }}>Development Value</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Demise</TableHead>
                          <TableHead style={{ textAlign: "right" }}>Units</TableHead>
                          <TableHead style={{ textAlign: "right" }}>NIA (sq ft)</TableHead>
                          <TableHead style={{ textAlign: "right" }}>Gross Rent PA</TableHead>
                          <TableHead style={{ textAlign: "right" }}>Net Rent PA</TableHead>
                          <TableHead style={{ textAlign: "right" }}>Gross CV</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {results.developer_appraisal?.blocks?.map((b) => (
                          <TableRow key={b.name}>
                            <TableCell style={{ fontWeight: 500 }}>{b.name}</TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right" }}>{b.units}</TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtNum(b.nia)}</TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(b.gross_rent_pa)}</TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(b.net_rent_pa)}</TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(b.gross_cv)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell style={{ fontWeight: 700 }}>Gross Dev Value</TableCell>
                          <TableCell colSpan={4} />
                          <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 700 }}>{fmtFull(results.developer_appraisal?.total_gdv)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ fontWeight: 700 }}>Net Dev Value</TableCell>
                          <TableCell colSpan={4} />
                          <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 700 }}>{fmtFull(results.developer_appraisal?.total_ndv)}</TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </CardContent>
                </Card>

                {/* Development Costs */}
                <Card>
                  <CardHeader><CardTitle style={{ fontSize: 15 }}>Development Costs</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableBody>
                        {["land", "construction", "soft"].map((cat) => (
                          <TableRow key={cat}>
                            <TableCell style={{ fontWeight: 600, textTransform: "uppercase", fontSize: 13 }}>
                              {cat === "soft" ? "Soft Costs" : cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 600 }}>
                              {fmtFull(results.developer_appraisal?.costs?.[cat]?.total)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell style={{ fontWeight: 700 }}>Total Costs (Excl. Finance)</TableCell>
                          <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 700 }}>
                            {fmtFull(results.developer_appraisal?.costs?.total_excl_finance)}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </CardContent>
                </Card>

                {/* Profit Summary */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div className="kpi-card">
                    <div className="kpi-label">Development Profit</div>
                    <div className="kpi-value" style={{ color: results.developer_appraisal?.profit >= 0 ? "#16a34a" : "#ef4444" }}>
                      {fmtFull(results.developer_appraisal?.profit)}
                    </div>
                    <div className="kpi-sub">{fmtPct(results.developer_appraisal?.profit_on_cost)} on cost</div>
                  </div>
                  <div className="kpi-card">
                    <div className="kpi-label">Ungeared IRR</div>
                    <div className="kpi-value">{fmtPct(kpis?.ungeared_irr)}</div>
                    <div className="kpi-sub">Multiple: {kpis?.ungeared_multiple?.toFixed(2)}x</div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="funder" style={{ marginTop: 20 }}>
              <Card>
                <CardHeader><CardTitle style={{ fontSize: 15 }}>Funder Appraisal</CardTitle></CardHeader>
                <CardContent>
                  <Table>
                    <TableBody>
                      {results.funder_appraisal?.items?.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell style={{ fontWeight: item.bold ? 700 : 400 }}>{item.label}</TableCell>
                          <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: item.bold ? 700 : 400 }}>
                            {fmtFull(item.value)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell style={{ fontWeight: 700 }}>Funder Profit</TableCell>
                        <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 700, color: results.funder_appraisal?.profit >= 0 ? "#16a34a" : "#ef4444" }}>
                          {fmtFull(results.funder_appraisal?.profit)}
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Footer */}
        <div style={{ marginTop: 48, paddingTop: 24, borderTop: "1px solid #e2e8f0", textAlign: "center" }}>
          <p style={{ fontSize: 12, color: "#94a3b8" }}>
            Generated by Toro Strategic Development Appraisal Platform
          </p>
        </div>
      </div>
    </div>
  );
}
